import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DashboardShell from "./DashboardShell";
import { toast } from "sonner";
import {
  CalendarDays, CalendarRange, PoundSterling, StickyNote, Loader2, Clock, Check, X, Plus,
} from "lucide-react";

const NAV = [
  { key: "today", label: "Today", icon: CalendarDays },
  { key: "week", label: "This Week", icon: CalendarRange },
  { key: "earnings", label: "Earnings", icon: PoundSterling },
  { key: "notes", label: "Customer Notes", icon: StickyNote },
];

const todayIso = () => new Date().toISOString().split("T")[0];

export default function BarberDashboard() {
  const { barbers, staff, staffLogout } = useAuth();
  const [active, setActive] = useState("today");
  const [barberId, setBarberId] = useState("");
  const [appts, setAppts] = useState(null);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => { if (barbers.length && !barberId) setBarberId(barbers[0].id); }, [barbers]);

  const load = () => {
    if (!barberId) return;
    api.get("/appointments/barber", { params: { barber_id: barberId } }).then((r) => setAppts(r.data)).catch(() => setAppts([]));
    api.get(`/barber/earnings/${barberId}`).then((r) => setEarnings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [barberId]);

  const setStatus = async (id, status) => {
    try { await api.put(`/appointments/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const today = appts?.filter((a) => a.date === todayIso() && a.status !== "cancelled") || [];
  const week = appts?.filter((a) => a.date >= todayIso() && a.status !== "cancelled") || [];

  const grouped = {};
  week.forEach((a) => { (grouped[a.date] = grouped[a.date] || []).push(a); });

  return (
    <DashboardShell testid="barber-dashboard" title="Barber" nav={NAV} active={active} onNav={setActive} identity={staff} onLogout={staffLogout}>
      <div className="mb-6 flex items-center gap-3">
        <span className="label-caps">Viewing</span>
        <select data-testid="barber-select" value={barberId} onChange={(e) => setBarberId(e.target.value)} className="bg-[#141414] border border-white/10 rounded-xl px-4 py-2 text-white focus:border-[#D4AF37] focus:ring-0">
          {barbers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {appts === null ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>
      ) : (
        <>
          {active === "today" && (
            today.length === 0 ? <Empty text="No appointments today." /> : (
              <div className="space-y-3">{today.sort((a, b) => a.start_time.localeCompare(b.start_time)).map((a) => <BarberApptCard key={a.id} a={a} setStatus={setStatus} />)}</div>
            )
          )}

          {active === "week" && (
            Object.keys(grouped).length === 0 ? <Empty text="No upcoming appointments this week." /> : (
              <div className="space-y-8">
                {Object.keys(grouped).sort().map((d) => (
                  <div key={d}>
                    <h3 className="label-caps mb-3">{new Date(d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" })}</h3>
                    <div className="space-y-3">{grouped[d].sort((a, b) => a.start_time.localeCompare(b.start_time)).map((a) => <BarberApptCard key={a.id} a={a} setStatus={setStatus} />)}</div>
                  </div>
                ))}
              </div>
            )
          )}

          {active === "earnings" && earnings && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <BigStat label="Today" value={`£${earnings.today}`} />
              <BigStat label="Last 7 Days" value={`£${earnings.week}`} />
              <BigStat label="All Time" value={`£${earnings.total}`} />
              <div className="sm:col-span-3 bg-[#141414] border border-white/5 rounded-2xl p-6"><div className="label-caps mb-1">Completed Cuts</div><div className="font-display text-4xl">{earnings.completed_count}</div></div>
            </div>
          )}

          {active === "notes" && <NotesPanel appts={appts} />}
        </>
      )}
    </DashboardShell>
  );
}

function NotesPanel({ appts }) {
  const customers = {};
  appts.forEach((a) => { if (a.customer_id) customers[a.customer_id] = a.customer_name; });
  const list = Object.entries(customers);
  const [sel, setSel] = useState("");
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");

  const load = (cid) => api.get(`/notes/${cid}`).then((r) => setNotes(r.data)).catch(() => setNotes([]));
  const pick = (cid) => { setSel(cid); load(cid); };
  const add = async () => {
    if (!text.trim() || !sel) return;
    try { await api.post("/notes", { customer_id: sel, text }); setText(""); load(sel); toast.success("Note added"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (list.length === 0) return <Empty text="No customers with bookings yet." />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="space-y-2">
        {list.map(([cid, name]) => (
          <button key={cid} data-testid={`note-customer-${cid}`} onClick={() => pick(cid)} className={`w-full text-left px-4 py-3 rounded-xl transition-colors duration-200 ${sel === cid ? "bg-[#D4AF37] text-black" : "bg-[#141414] text-gray-300 hover:bg-white/5"}`}>{name}</button>
        ))}
      </div>
      <div className="md:col-span-2">
        {sel ? (
          <div>
            <div className="flex gap-2 mb-5">
              <input data-testid="note-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note (e.g. prefers scissors over clippers)" className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-0" />
              <button data-testid="note-add" onClick={add} className="px-5 rounded-xl bg-[#D4AF37] text-black"><Plus className="w-5 h-5" /></button>
            </div>
            {notes.length === 0 ? <p className="text-gray-500">No notes yet.</p> : (
              <div className="space-y-3">{notes.map((n) => <div key={n.id} className="bg-[#141414] border border-white/5 rounded-xl p-4"><p className="text-gray-300">{n.text}</p><div className="text-xs text-gray-500 mt-2">{n.author} · {new Date(n.created_at).toLocaleDateString()}</div></div>)}</div>
            )}
          </div>
        ) : <p className="text-gray-500">Select a customer to view notes.</p>}
      </div>
    </div>
  );
}

const Empty = ({ text }) => (<div className="flex flex-col items-center py-20 text-gray-500"><CalendarDays className="w-12 h-12 mb-4 opacity-50" />{text}</div>);
const BigStat = ({ label, value }) => (<div className="bg-[#141414] border border-white/5 rounded-2xl p-6"><div className="label-caps mb-1">{label}</div><div className="font-display text-4xl text-[#D4AF37]">{value}</div></div>);

const BarberApptCard = ({ a, setStatus }) => (
  <div data-testid={`barber-appt-${a.id}`} className="bg-[#141414] border border-white/5 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
    <div className="flex items-center gap-4">
      <div className="text-center w-16 shrink-0"><div className="font-display text-2xl text-[#D4AF37]">{a.start_time}</div><div className="text-xs text-gray-500 flex items-center justify-center gap-1"><Clock className="w-3 h-3" />{a.duration}m</div></div>
      <div><div className="text-lg">{a.customer_name}{a.status === "walk-in" && <span className="text-xs ml-2 text-[#D4AF37]">walk-in</span>}</div><div className="text-gray-400 text-sm">{a.service_name} · £{a.price}</div>{a.notes && <div className="text-xs text-gray-500 mt-1 italic">"{a.notes}"</div>}</div>
    </div>
    <div className="flex items-center gap-2">
      {a.status === "completed" ? <span className="text-xs px-3 py-1 rounded-full bg-green-500/10 text-green-400">completed</span> : (
        <>
          <button data-testid={`complete-${a.id}`} onClick={() => setStatus(a.id, "completed")} className="px-4 py-2 rounded-full bg-green-500/10 text-green-400 text-sm flex items-center gap-1 hover:bg-green-500/20 transition-colors duration-200"><Check className="w-4 h-4" /> Complete</button>
          <button data-testid={`noshow-${a.id}`} onClick={() => setStatus(a.id, "no-show")} className="px-3 py-2 rounded-full bg-white/5 text-gray-400 text-sm hover:bg-white/10 transition-colors duration-200"><X className="w-4 h-4" /></button>
        </>
      )}
    </div>
  </div>
);
